import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Plus, Edit, Printer, Trash2, Loader2, FileText, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ManifestPrint from './ManifestPrint';

const ManifestList = () => {
  const { docketId } = useParams();
  const [searchParams] = useSearchParams();
  const docketIdFromQuery = searchParams.get('docket_id');
  const finalDocketId = docketId || docketIdFromQuery;
  
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [manifests, setManifests] = useState([]);
  const [docketInfo, setDocketInfo] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [printMode, setPrintMode] = useState(() => {
    // Load saved preference or default to 'with-header'
    return localStorage.getItem('manifest_print_mode') || 'with-header';
  });
  const [showPrintView, setShowPrintView] = useState(false);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });
  
  useEffect(() => {
    loadManifests();
    loadDocketInfo();
  }, [finalDocketId]);
  
  const loadDocketInfo = async () => {
    if (!finalDocketId) return;
    try {
      const res = await fetch(`${backendUrl}/api/dockets/${finalDocketId}`, { headers: getAuth() });
      if (res.ok) {
        const data = await res.json();
        setDocketInfo(data);
      }
    } catch (e) {
      console.error('Failed to load docket info:', e);
    }
  };
  
  const loadManifests = async () => {
    if (!finalDocketId) {
      toast({ title: 'Error', description: 'Docket ID is required', variant: 'destructive' });
      return;
    }
    
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/air-outward-manifests?docket_id=${finalDocketId}`, { headers: getAuth() });
      if (!res.ok) throw new Error('Failed to load manifests');
      const data = await res.json();
      setManifests(data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (manifestId) => {
    if (!window.confirm('Are you sure you want to delete this manifest?')) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/air-outward-manifests/${manifestId}`, {
        method: 'DELETE',
        headers: getAuth()
      });
      if (!res.ok) throw new Error('Failed to delete manifest');
      toast({ title: 'Success', description: 'Manifest deleted successfully' });
      loadManifests();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };
  
  const handlePrintClick = (manifest) => {
    setSelectedManifest(manifest);
    setShowPrintModal(true);
  };
  
  const handlePrintConfirm = () => {
    // Save user preference
    localStorage.setItem('manifest_print_mode', printMode);
    setShowPrintModal(false);
    setShowPrintView(true);
  };
  
  const handleClosePrint = () => {
    setShowPrintView(false);
    setSelectedManifest(null);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN');
    } catch {
      return dateStr;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  // Print View
  if (showPrintView && selectedManifest) {
    return (
      <div className="min-h-screen print:min-h-0 bg-gray-50/50 p-6 print:p-0 print:bg-white">
        <div className="print:hidden sticky top-0 z-50 flex justify-between items-center bg-white shadow-sm border border-gray-200 p-4 rounded-xl mb-6 mx-auto" style={{ maxWidth: '210mm' }}>
          <Button variant="outline" onClick={handleClosePrint}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <div className="flex gap-4 items-center">
            <span className="text-sm text-gray-600 flex items-center">
              Mode: <strong className="ml-1">{printMode === 'with-header' ? 'With Header/Footer' : 'Without Header/Footer'}</strong>
            </span>
            <Button 
              onClick={() => {
                const originalTitle = document.title;
                const docketNo = docketInfo?.system_docket_no || finalDocketId || 'UNKNOWN';
                document.title = `Console Manifest - ${docketNo}`;
                window.print();
                setTimeout(() => {
                  document.title = originalTitle;
                }, 1000);
              }} 
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
        <div className="print:pt-0 mx-auto" style={{ maxWidth: '210mm' }}>
          <ManifestPrint manifestId={selectedManifest.id} manifestData={selectedManifest} printMode={printMode} />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Air Outward Manifest List
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Docket: {docketInfo?.system_docket_no || finalDocketId}
                {manifests.length > 0 && ` • ${manifests.length} Manifest${manifests.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/air-manifests/new?docket_id=${finalDocketId}`)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Manifest
          </Button>
        </div>
        
        {/* Manifests Table */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-purple-600 to-purple-500 text-white">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Sr. No.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Shipper</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">MAWB No.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">No. of HAWB</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Freight</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Consignee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">HAWB No.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Flight</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {manifests.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p className="font-medium">No manifests found</p>
                      <p className="text-sm">Click "Add New Manifest" to create your first manifest</p>
                    </td>
                  </tr>
                ) : (
                  manifests.map((manifest, idx) => (
                    <tr key={manifest.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-700">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{manifest.shipper_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{manifest.mawb_no || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{manifest.no_of_hawb || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{manifest.freight || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{manifest.consignee_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{manifest.hawb_no || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{manifest.flight || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(manifest.manifest_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/air-manifests/${manifest.id}/edit`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePrintClick(manifest)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Print"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(manifest.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
        
        {/* Print Mode Selection Modal */}
        {showPrintModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Select Print Mode</h2>
                <button onClick={() => setShowPrintModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3 mb-6">
                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                  style={{ borderColor: printMode === 'with-header' ? '#9333ea' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="printMode"
                    value="with-header"
                    checked={printMode === 'with-header'}
                    onChange={(e) => setPrintMode(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-semibold text-gray-800">With Header & Footer</div>
                    <div className="text-sm text-gray-600">Includes company logo, address, GST, and contact details</div>
                  </div>
                </label>
                
                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors"
                  style={{ borderColor: printMode === 'without-header' ? '#9333ea' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="printMode"
                    value="without-header"
                    checked={printMode === 'without-header'}
                    onChange={(e) => setPrintMode(e.target.value)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-semibold text-gray-800">Without Header & Footer</div>
                    <div className="text-sm text-gray-600">Clean layout with only manifest data</div>
                  </div>
                </label>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowPrintModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handlePrintConfirm} className="flex-1 bg-purple-600 hover:bg-purple-700">
                  <Printer className="w-4 h-4 mr-2" />
                  Continue to Print
                </Button>
              </div>
            </Card>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default ManifestList;
